'use client'
import { DarkButton } from "@/ui/Button"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"

// 页面加载时所用的版本号（构建期注入）
const CURRENT_VERSION = process.env.NEXT_PUBLIC_BUILD_ID || ''
// 轮询间隔：5 分钟
const CHECK_INTERVAL = 5 * 60 * 1000

/**
 * 新版本上线提醒：
 * 定时（以及切回页面时）请求 /api/version，与当前页面加载的版本比对，
 * 不一致说明已发布新版本，弹出与主题一致的横幅提示让用户刷新。
 */
export const VersionChecker = () => {
  const t = useTranslations()
  const [show, setShow] = useState(false)
  // 已检测到新版本则停止后续轮询/提醒
  const notifiedRef = useRef(false)

  useEffect(() => {
    if (!CURRENT_VERSION) return

    let stopped = false

    const checkVersion = async () => {
      if (notifiedRef.current || stopped || document.visibilityState !== 'visible') return
      try {
        const res = await fetch('/api/version', { cache: 'no-store' })
        if (!res.ok) return
        const { version } = await res.json()
        if (!version || version === CURRENT_VERSION) return
        notifiedRef.current = true
        setShow(true)
      } catch {
        // 网络错误忽略，等待下次轮询
      }
    }

    const timer = setInterval(checkVersion, CHECK_INTERVAL)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    // 首次延迟检查，避开首屏加载
    const firstCheck = setTimeout(checkVersion, 10 * 1000)

    return () => {
      stopped = true
      clearInterval(timer)
      clearTimeout(firstCheck)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  if (!show) return null

  return (
    <div className="fixed z-[100] bottom-4 inset-x-4 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[420px] animate__animated animate__fadeInUp">
      <div className="flex items-center gap-3 rounded-2xl border border-theme-primary/10 bg-content1 px-4 py-3 shadow-xl">
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-bold text-theme-primary truncate">{t('common.update.title')}</p>
          <p className="mt-0.5 text-[13px] text-theme-primary/60 line-clamp-1">{t('common.update.desc')}</p>
        </div>
        <DarkButton size="sm" className="flex-shrink-0 px-5" onPress={() => window.location.reload()}>
          {t('common.update.refresh')}
        </DarkButton>
        <button
          type="button"
          aria-label="close"
          className="flex-shrink-0 text-theme-primary/40 transition-colors hover:text-theme-primary/80"
          onClick={() => setShow(false)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  )
}
